import React from 'react';
import { PanelLeft } from 'lucide-react';
import { useContent } from '../contexts/ContentContext';
import { useSidebar } from '../contexts/SidebarContext';
import { pageTitle, resolvePage } from '../pages';
import { useIllustrationIndex } from '../hooks/useIllustrationIndex';
import { archiveUrl } from '../hooks/useArchiveYears';
import { useVisitorFavorites } from '../hooks/useVisitorFavorites';
import { illustrationHome, illustrationUrl, resolveIllustrationRoute } from '../utils/illustrationRoute';
import { SEARCH_KEYS } from '../utils/publicSearch';
import GuideHelpButton from './GuideHelpButton';
import { GuideHelpId } from '../utils/guideHelp';

interface PageHeaderProps {
    title: React.ReactNode;
    breadcrumb?: React.ReactNode;
    count?: React.ReactNode;
    description?: React.ReactNode;
    actions?: React.ReactNode;
    tools?: React.ReactNode;
    children?: React.ReactNode;
    helpSection?: GuideHelpId;
    compactHeading?: boolean;
}

export function PageHeader({ title, breadcrumb, count, description, actions, tools, children, helpSection, compactHeading = false }: PageHeaderProps) {
    return <header className={`lab-content-header${compactHeading ? ' lab-content-header-compact' : ''}`}>
        {compactHeading && <h1 className="sr-only" tabIndex={-1}>{title}</h1>}
        {breadcrumb}
        {(!compactHeading || actions || helpSection) && <div className="lab-content-heading">
            {!compactHeading && <div className="lab-content-identity"><h1 title={typeof title === 'string' ? title : undefined}>{title}</h1>{count != null && <span className="lab-content-count" role="status">{count}</span>}</div>}
            {(actions || helpSection) && <div className="lab-content-actions">{actions}{helpSection && <GuideHelpButton section={helpSection} />}</div>}
        </div>}
        {(description || tools) && <div className="lab-content-support">
            {description && <p className="lab-content-description">{description}</p>}
            {tools && <div className="lab-content-tools">{tools}</div>}
        </div>}
        {children && <div className="lab-content-context">{children}</div>}
    </header>;
}

function IllustrationListHeader(props: Omit<PageHeaderProps, 'title' | 'breadcrumb'>) {
    const { content, language, t } = useContent();
    const { data } = useIllustrationIndex();
    const favorites = useVisitorFavorites();
    const params = new URLSearchParams(window.location.search);
    const root = resolveIllustrationRoute(window.location.search) || 'search';
    const homeLabel = pageTitle(resolvePage('?page=illustrations').definition, content, language, t);
    const archive = root === 'recent' && params.has('year');
    const rootLabel = root === 'home' ? homeLabel : root === 'search' ? t('illustrations.searchResults') : archive ? t('illustrations.byYear') : t(`illustrations.${root}`);
    const crumbs = [{ label: homeLabel, href: illustrationHome }];
    const conditions: string[] = [];
    const query = new URLSearchParams({ page: root === 'favorites' ? 'favorites' : root === 'recent' || root === 'popular' ? 'gallery' : 'search' });
    if (root !== 'home' && root !== 'favorites') query.set('root', root);
    if (root !== 'home') crumbs.push({ label: rootLabel, href: archive && params.get('month') ? archiveUrl(params.get('year')!, params.get('month')!) : root === 'search' ? '/?page=search' : illustrationUrl(root) });
    const add = (label: string, values: Record<string, string>) => {
        Object.entries(values).forEach(([key, value]) => query.set(key, value));
        conditions.push(label); crumbs.push({ label, href: '/?' + query });
    };
    if (root === 'favorites') {
        const id = params.get('collection');
        if (id) add(favorites.data.collections.find(item => item.id === id)?.name || t('favorites.missingCollection'), { collection: id });
        else if (params.get('unfiled') === '1') add(t('illustrations.unfiled'), { unfiled: '1' });
    } else if (params.get('page') === 'search' || params.get('search') === '1') {
        const keys = [...SEARCH_KEYS].sort((a, b) => Number(b === root) - Number(a === root));
        for (const key of keys) {
            const value = params.get(key);
            if (!value) continue;
            const group = key === 'character' ? t('illustrations.character') : content.ui[language].search.group[key as keyof typeof content.ui.jp.search.group];
            const name = key === 'rating' ? (/^[1-5]$/.test(value) ? `★${value}` : value)
                : data?.groups[key]?.values.find(item => item.slug === value)?.label || value;
            add(key === root ? name : `${group}: ${name}`, { [key]: value });
        }
        if (params.has('year')) add(params.has('month') ? t('search.month', { year: params.get('year')!, month: params.get('month')! }) : `${t('illustrations.year')}: ${params.get('year')!}`, { year: params.get('year')!, ...(params.has('month') ? { month: params.get('month')! } : {}) });
        if (params.has('tags')) add(`${t('header.word')}: ${params.get('tags')!}`, { tags: params.get('tags')! });
        if (params.has('color')) add(`${t('common.color')}: ${params.get('color')!}`, { color: params.get('color')! });
    } else if (root !== 'home') {
        if (root === 'recent' && params.get('year')) {
            const year = params.get('year')!, month = params.get('month');
            add(month ? t('search.month', { year, month }) : `${t('illustrations.year')}: ${year}`, { year, ...(month ? { month } : {}) });
        }
        if (params.get('page') === 'word') {
            const word = params.get('word') || params.get('tags')?.split(',')[0];
            if (word) { query.set('page', 'word'); add(`${t('header.word')}: ${word}`, { word }); }
        }
        if (params.get('page') === 'color' && params.get('color')) {
            query.set('page', 'color'); add(`${t('common.color')}: ${params.get('color')!}`, { color: params.get('color')! });
        }
    }
    const tagWord = params.get('page') === 'word' ? params.get('word') || params.get('tags')?.split(',')[0] : null;
    return <PageHeader {...props} compactHeading title={tagWord ? t('illustrations.tagTitle', { word: tagWord }) : conditions.length ? t('common.wordTitle', { label: rootLabel, word: conditions.join(' / ') }) : rootLabel}
        breadcrumb={<nav className="lab-content-breadcrumb" aria-label={t('common.breadcrumb')}>
            {crumbs.map((crumb, index) => <React.Fragment key={index}>
                {index > 0 && <span aria-hidden="true">/</span>}
                {index === crumbs.length - 1 ? <span aria-current="page">{crumb.label}</span> : <a href={crumb.href}>{crumb.label}</a>}
            </React.Fragment>)}
            {props.count != null && <span className="lab-content-count" role="status">{props.count}</span>}
        </nav>} />;
}

export default function ContentPageHeader({ title, sidebar = false, actions, ...props }: Omit<PageHeaderProps, 'title' | 'breadcrumb'> & { title?: React.ReactNode; sidebar?: boolean }) {
    const { content, language, t } = useContent();
    const { isSidebarOpen, toggleSidebar } = useSidebar();
    const route = resolvePage(window.location.search);
    sidebar = sidebar && route.definition.kind !== 'images';
    const label = pageTitle(route.definition, content, language, t);
    const detail = ['image', 'song', 'detail'].includes(route.view || '');
    const helpSection: GuideHelpId | undefined = route.definition.kind === 'images' ? (detail ? 'image-detail' : 'search')
        : route.definition.kind === 'works' ? 'works'
        : route.definition.kind === 'article' ? 'articles'
        : ['twitch_guide', 'command_editor'].includes(route.definition.id) ? 'twitch'
        : route.definition.id === 'tool' ? 'tools' : route.definition.id === 'about' ? 'about' : undefined;
    if (route.definition.kind === 'images' && !detail && !['workflow', 'ratings'].includes(route.page || '')) {
        return <IllustrationListHeader {...props} actions={actions} />;
    }
    const sidebarLabel = route.page === 'article' ? t('navigation.articleSidebar')
        : route.tab === 'works' ? t('navigation.workSidebar') : t('navigation.imageSidebar');
    return <PageHeader helpSection={helpSection} {...props} title={title ?? label}
        breadcrumb={<nav className="lab-content-breadcrumb" aria-label={t('common.breadcrumb')}>
            <a href="/">{content.ui[language].siteMap.topPage}</a><span aria-hidden="true">/</span>
            {detail ? <><a href={route.definition.href}>{label}</a><span aria-hidden="true">/</span><span aria-current="page">{t('navigation.detail')}</span></> : <span aria-current="page">{label}</span>}
        </nav>}
        actions={sidebar || actions ? <>{sidebar && <button type="button" className="lab-button" onClick={toggleSidebar} aria-expanded={isSidebarOpen}><PanelLeft aria-hidden="true" />{sidebarLabel}</button>}{actions}</> : undefined}
    />;
}
